import { right, left, chain, map, getOrElse, either, } from 'fp-ts/lib/Either';
import { sequenceT, sequenceS } from 'fp-ts/lib/Apply';
import { pipe } from 'fp-ts/lib/pipeable';
import { SquareMatrix3D, Matrix, sum, inverse as inverseS, multiply, } from '../Matrix';
import { Vector, Vector3D } from '../Vector';
import { Position3D, Position2D } from '../Position';
import { AxisAlignedBoundingBox } from '../AxisAlignedBoundingBox';
// TODO: move this to AffineTransform.ts
export class AffineTransform3D {
    _transform;
    _translation;
    /**
     * return the identity transform
     */
    static identity() {
        return new AffineTransform3D(SquareMatrix3D.identity(), new Vector(3, [0, 0, 0]));
    }
    constructor(_transform, _translation) {
        this._transform = _transform;
        this._translation = _translation;
    }
    inverse() {
        return pipe(this._transform, inverseS, chain(inverseMatrix => pipe(inverseMatrix, multiply(new Matrix(3, 1, this._translation.value())), 
        // chain runs a fn `across` an Either, which means that
        // if (and only if) the Either is a Right, the Either is
        // unwrapped and passed to the function, and expects you to
        // return another Either. This allows us to 'merge' two Eithers
        // together.
        // it behaves like map, in that this will only run if the Either is
        // a Right
        chain(Vector.fromMatrix), chain(translationVector => right(new AffineTransform3D(inverseMatrix, translationVector))))));
    }
    position(pos) {
        return pipe(this._transform, multiply(Matrix.columnFromPos3D(pos)), chain(sum(Matrix.columnFromVector(this._translation))), chain(Position3D.fromMatrix));
    }
    xzPosition(pos) {
        return pipe(this._transform, multiply(Matrix.columnFromPos3D(pos)), chain(sum(Matrix.columnFromVector(this._translation))), chain(Position2D.xzFromMatrix));
    }
    xyPosition(pos) {
        return pipe(this._transform, multiply(Matrix.columnFromPos3D(pos)), chain(sum(Matrix.columnFromVector(this._translation))), chain(Position2D.xyFromMatrix));
    }
    vector(vec) {
        return pipe(this._transform, multiply(Matrix.columnFromVector(vec)), chain(Vector3D.fromMatrix));
    }
    cornersFromAxisAlignedBoundingBox(aabb) {
        return this.rawCornersFromAxisAlignedBoundingBox(aabb).reduce((result, eitherPos) => {
            const res = pipe(result, getOrElse(() => []));
            return pipe(eitherPos, map(pos => res.concat(pos)));
        }, left(''));
    }
    xzVerticesFromAxisAlignedBoundingBox(aabb) {
        return this.rawXZCornersFromAxisAlignedBoundingBox(aabb).reduce((result, eitherPos) => {
            const res = pipe(result, getOrElse(() => []));
            return pipe(eitherPos, map(pos => res.concat(pos)));
        }, left(''));
    }
    xyVerticesFromAxisAlignedBoundingBox(aabb) {
        return this.rawXYCornersFromAxisAlignedBoundingBox(aabb).reduce((result, eitherPos) => {
            const res = pipe(result, getOrElse(() => []));
            return pipe(eitherPos, map(pos => res.concat(pos)));
        }, left(''));
    }
    /**
     * given an AxisAlignedBoundingBox, return a new AxisAlignedBoundingBox after the transform has been applied
     */
    axisAlignedBoundingBox(aabb) {
        const minmax = ([min, max], curr) => [curr.minimum(min), curr.maximum(max)];
        return pipe(this.rawCornersFromAxisAlignedBoundingBox(aabb).reduce((result, eitherPos) => {
            return pipe(
            // sequenceT pulls both Eithers into a single Either tuple,
            sequenceT(either)(result, eitherPos), map(([res, pos]) => minmax(res, pos)));
        }, right([
            new Position3D(Infinity, Infinity, Infinity),
            new Position3D(-Infinity, -Infinity, -Infinity),
        ])), map(([min, max]) => new AxisAlignedBoundingBox(min, max)));
    }
    axes() {
        return sequenceS(either)({
            x: this.vector(Vector3D.unitX()),
            y: this.vector(Vector3D.unitY()),
            z: this.vector(Vector3D.unitZ()),
        });
    }
    rawCornersFromAxisAlignedBoundingBox(aabb) {
        return aabb.corners().map(pos => this.position(pos));
    }
    rawXZCornersFromAxisAlignedBoundingBox(aabb) {
        return pipe(aabb.corners(), corners => [
            corners[0], //left-bottom-rear
            corners[1], //right-bottom-rear
            corners[5], //right-bottom-front
            corners[4], //left-bottom-front
        ]).map(pos => this.xzPosition(pos));
    }
    rawXYCornersFromAxisAlignedBoundingBox(aabb) {
        return pipe(aabb.corners(), corners => [
            corners[0], //left-bottom-rear
            corners[1], //right-bottom-rear
            corners[3], //right-top-rear
            corners[2], //left-top-rear
        ]).map(pos => this.xyPosition(pos));
    }
}
export const inverse = (a) => a.inverse();
//# sourceMappingURL=index.js.map